import { spaceTextToStartupArray } from '@/components-biz/ImageSelect/utils/containerStartup';

import type { CommandShellType } from './types';

/** API 初始化命令结构 */
export interface MaasInitCommandApi {
  commands: string[];
  args?: string[];
}

/** 表单侧初始化命令辅助字段 */
export interface MaasInitCommandFormFields {
  _shellType?: CommandShellType;
  _commandsText?: string;
  _argsText?: string;
}

const SHELL_PREFILL: Record<CommandShellType, string> = {
  bash: 'bash -c',
  sh: 'sh -c',
};

/** 判断命令文本是否为 shell 预填值（bash -c / sh -c） */
export function isShellPrefillText(text: unknown): boolean {
  const trimmed = String(text ?? '').trim();
  return (Object.values(SHELL_PREFILL) as string[]).includes(trimmed);
}

/** 空格分隔命令文本 → commands 数组 */
export function spaceTextToCommands(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item ?? '').trim()).filter(Boolean);
  }
  if (typeof raw !== 'string') return [];
  return spaceTextToStartupArray(raw);
}

/** 多行参数文本 → args 数组（每行一个 token，忽略空行） */
export function multilineTextToArgs(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item ?? '').trim()).filter(Boolean);
  }
  if (typeof raw !== 'string') return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

/** 脚本文本 → args 数组（整段脚本存入 args[0]） */
export function scriptTextToArgs(raw: unknown): string[] {
  const text = String(raw ?? '').trim();
  return text ? [text] : [];
}

/** args 数组 → 脚本文本（兼容历史多参数按行拼接展示） */
export function argsToScriptText(args: string[] | undefined): string | undefined {
  if (!args?.length) return undefined;
  if (args.length === 1) return args[0];
  return args.join('\n');
}

/** commands 数组 → 空格分隔展示文本 */
export function commandsToSpaceText(commands: string[] | undefined): string | undefined {
  if (!commands?.length) return undefined;
  return commands.join(' ');
}

/** args 数组 → 多行展示文本 */
export function argsToMultilineText(args: string[] | undefined): string | undefined {
  if (!args?.length) return undefined;
  return args.join('\n');
}

/** 从 commands 数组推断 shell 类型 */
export function detectShellType(commands: string[] | undefined): CommandShellType {
  const first = String(commands?.[0] ?? '')
    .trim()
    .toLowerCase();
  if (first === 'sh') return 'sh';
  return 'bash';
}

/** 选中 shell 类型时的预填命令文本（commands 为空时使用） */
export function applyShellPrefill(type: CommandShellType): string {
  return SHELL_PREFILL[type];
}

/** 从 API initCommand 解析脚本文本（兼容 shell 包装与非 shell 历史数据） */
export function resolveScriptTextFromInitCommand(initCommand?: MaasInitCommandApi | null): string | undefined {
  if (!initCommand?.commands?.length && !initCommand?.args?.length) return undefined;
  const commandsText = commandsToSpaceText(initCommand?.commands) ?? '';
  const args = initCommand?.args ?? [];
  if (isShellPrefillText(commandsText)) {
    return argsToScriptText(args);
  }
  const commandLine = [commandsText, ...args].join(' ').trim();
  return commandLine || undefined;
}

/** API initCommand → 表单辅助字段 */
export function initCommandApiToFormFields(initCommand?: MaasInitCommandApi | null): MaasInitCommandFormFields {
  if (!initCommand?.commands?.length && !initCommand?.args?.length) {
    return {};
  }
  const commands = initCommand?.commands ?? [];
  const shellType = isShellPrefillText(commandsToSpaceText(commands)) ? detectShellType(commands) : 'bash';
  return {
    _shellType: shellType,
    _commandsText: applyShellPrefill(shellType),
    _argsText: resolveScriptTextFromInitCommand(initCommand),
  };
}

/** 表单辅助字段 → API initCommand；无有效内容时返回 undefined */
export function initCommandFormFieldsToApi(fields: MaasInitCommandFormFields | undefined): MaasInitCommandApi | undefined {
  if (!fields) return undefined;
  const shellType = fields._shellType ?? 'bash';
  const commands = spaceTextToCommands(applyShellPrefill(shellType));
  const args = scriptTextToArgs(fields._argsText);
  if (!args.length) return undefined;
  return { commands, args };
}

/** 判断 initCommand 表单是否有内容 */
export function hasInitCommandFormValue(fields: MaasInitCommandFormFields | undefined): boolean {
  if (!fields) return false;
  return scriptTextToArgs(fields._argsText).length > 0;
}

/** initCommand API → 完整命令行展示 */
export function formatInitCommandDisplay(initCommand?: MaasInitCommandApi | null): string {
  if (!initCommand) return '-';
  const commandsText = commandsToSpaceText(initCommand.commands) ?? '';
  const script = argsToScriptText(initCommand.args);
  if (isShellPrefillText(commandsText)) {
    if (!script) return commandsText || '-';
    return `${commandsText} ${script}`;
  }
  const parts = [...(initCommand.commands ?? []), ...(initCommand.args ?? [])];
  if (!parts.length) return '-';
  return parts.join(' ');
}
